import { RiskManagementFiltersContext } from "./context";
import type {
  ChartsView,
  RiskAssetFiltersState,
  RiskManagementFilters,
} from "./types";
import { useCallback, useMemo, useState } from "react";
import { useLazyQuery, useQuery } from "@apollo/client/react";
import { type FilterItemProps } from "@jana-technology/platform-ui";
import {
  AssetSelectionFilterDocument,
  WellNamesGroupedByHierarchyDocument,
} from "$/__generated__/graphql";
import { useGlobalFiltersContext } from "../global-filters/context";
import {
  deriveRadioTreeBreadcrumbs,
  flattenCheckboxValues,
  initHierarchyIdsCheckedState,
  joinHierarchyCheckboxesWithWellIds,
} from "../helpers";
import type { FilteredWellNamesByHierarchy } from "../types";
import {
  flattenRadioState,
  generateRadioState,
  initSelectedRiskAssetFiltersState,
  isRadioStateBreadcrumbsToLeaf,
} from "./helpers";

const baseRiskAssetFiltersState = {
  mostRecentCorrosionLogs: [],
  wellAgeRange: [],
  wellConfigurations: [],
  wellTypes: [],
  riskQuartiles: [],
} satisfies RiskAssetFiltersState;

export const RiskManagementFiltersProvider = ({
  children,
}: React.PropsWithChildren) => {
  //#region Get context values
  const {
    appliedWell,
    flattenedHierarchyIds,
    hierarchyIdCheckboxes,
    appliedHierarchyIdCheckboxStatesRisk,
  } = useGlobalFiltersContext();
  const [appliedHierarchyId, appliedWellName] = appliedWell;
  //#endregion

  const assetSelectionFilterQueryResult = useQuery(
    AssetSelectionFilterDocument,
  );

  const { assetSelectionFilter } = assetSelectionFilterQueryResult.data ?? {};

  const initialRiskAssetFilters = useMemo(
    () =>
      assetSelectionFilter
        ? initSelectedRiskAssetFiltersState(assetSelectionFilter)
        : baseRiskAssetFiltersState,
    [assetSelectionFilter],
  );

  // separate client and server data, rendering default from server until user modifies filters
  // necessary because server data isn't available immediately at mount + avoids useEffect
  const [
    selectedRiskAssetFiltersInternal,
    setSelectedRiskAssetFiltersInternal,
  ] = useState<RiskAssetFiltersState>();
  const [savedRiskAssetFiltersInternal, setSavedRiskAssetFiltersInternal] =
    useState<RiskAssetFiltersState>();

  const selectedRiskAssetFilters =
    selectedRiskAssetFiltersInternal ?? initialRiskAssetFilters;
  const setSelectedRiskAssetFilters: React.Dispatch<
    React.SetStateAction<RiskAssetFiltersState>
  > = useCallback(
    (nextState) => {
      setSelectedRiskAssetFiltersInternal((currentState) =>
        typeof nextState === "function"
          ? nextState(currentState ?? initialRiskAssetFilters)
          : nextState,
      );
    },
    [initialRiskAssetFilters],
  );
  const savedRiskAssetFilters =
    savedRiskAssetFiltersInternal ?? initialRiskAssetFilters;
  const setSavedRiskAssetFilters: React.Dispatch<
    React.SetStateAction<RiskAssetFiltersState>
  > = useCallback(
    (nextState) => {
      setSavedRiskAssetFiltersInternal((currentState) =>
        typeof nextState === "function"
          ? nextState(currentState ?? initialRiskAssetFilters)
          : nextState,
      );
    },
    [initialRiskAssetFilters],
  );

  const activeFilterCount = useMemo(() => {
    if (!assetSelectionFilter) {
      return 0;
    }

    const savedRiskAssetFiltersKeys = Object.keys(
      savedRiskAssetFilters,
    ) as Array<keyof RiskAssetFiltersState>;

    return savedRiskAssetFiltersKeys.reduce((count, key) => {
      if (key === "wellAgeRange" && assetSelectionFilter.wellAgeRange) {
        count += Number(
          !savedRiskAssetFilters.wellAgeRange.every((age) =>
            assetSelectionFilter.wellAgeRange.includes(age),
          ),
        );
      }

      if (
        (key === "mostRecentCorrosionLogs" || key === "wellConfigurations") &&
        assetSelectionFilter.mostRecentCorrosionLogs &&
        assetSelectionFilter.wellConfigurations
      ) {
        count += Number(
          assetSelectionFilter[key].length !==
            savedRiskAssetFilters[key].length,
        );
      }

      if (
        (key === "wellTypes" || key === "riskQuartiles") &&
        assetSelectionFilter.riskQuartiles &&
        assetSelectionFilter.wellTypes
      ) {
        count += Number(
          savedRiskAssetFilters[key].length !==
            assetSelectionFilter[key].length,
        );
      }

      return count;
    }, 0);
  }, [assetSelectionFilter, savedRiskAssetFilters]);

  const {
    mostRecentCorrosionLogs,
    riskQuartiles,
    wellAgeRange,
    wellConfigurations,
    wellTypes,
  } = initialRiskAssetFilters;

  // The declarative query provides the initial server-backed radio options.
  const wellNamesByHierarchyRiskChartsQueryResult = useQuery(
    WellNamesGroupedByHierarchyDocument,
    {
      skip: !assetSelectionFilter,
      variables: {
        hierarchyIds: flattenedHierarchyIds,
        wellConfigurations: wellConfigurations.length
          ? wellConfigurations
          : undefined,
        mostRecentCorrosionLogs: mostRecentCorrosionLogs.length
          ? mostRecentCorrosionLogs
          : undefined,
        quartileId:
          riskQuartiles.length > 0 && riskQuartiles.length !== 4
            ? riskQuartiles[0].id
            : undefined,
        wellAgeFrom: wellAgeRange[0],
        wellAgeTo: wellAgeRange[1],
        wellType: wellTypes.length === 1 ? wellTypes[0] : undefined,
        isOverall: true,
      },
    },
  );

  const wellNamesGroupedByHierarchyRiskCharts =
    wellNamesByHierarchyRiskChartsQueryResult.data?.wellNamesGroupedByHierarchy;

  const initialFilteredWellNamesByHierarchyRiskCharts = useMemo(
    () =>
      wellNamesGroupedByHierarchyRiskCharts?.filter(
        (item): item is FilteredWellNamesByHierarchy[number] =>
          typeof item.hierarchyId === "number",
      ) ?? [],
    [wellNamesGroupedByHierarchyRiskCharts],
  );

  const [
    filteredWellNamesByHierarchyRiskChartsInternal,
    setFilteredWellNamesByHierarchyRiskChartsInternal,
  ] = useState<FilteredWellNamesByHierarchy>();

  const filteredWellNamesByHierarchyRiskCharts =
    filteredWellNamesByHierarchyRiskChartsInternal ??
    initialFilteredWellNamesByHierarchyRiskCharts;

  const setFilteredWellNamesByHierarchyRiskCharts: RiskManagementFilters["setFilteredWellNamesByHierarchyRiskCharts"] =
    useCallback(
      (nextState) => {
        setFilteredWellNamesByHierarchyRiskChartsInternal((currentState) =>
          typeof nextState === "function"
            ? nextState(
                currentState ?? initialFilteredWellNamesByHierarchyRiskCharts,
              )
            : nextState,
        );
      },
      [initialFilteredWellNamesByHierarchyRiskCharts],
    );

  const [fetchWellNamesByHierarchyRiskCharts] = useLazyQuery(
    WellNamesGroupedByHierarchyDocument,
  );

  const hierarchiesWithWellNamesRadios = useMemo(
    () =>
      joinHierarchyCheckboxesWithWellIds(
        hierarchyIdCheckboxes,
        filteredWellNamesByHierarchyRiskCharts,
      ),
    [hierarchyIdCheckboxes, filteredWellNamesByHierarchyRiskCharts],
  );

  //#region Define states
  const initialSelectedHierarchyIdCheckboxStatesRisk = useMemo(() => {
    return (
      appliedHierarchyIdCheckboxStatesRisk ??
      initHierarchyIdsCheckedState(flattenCheckboxValues(hierarchyIdCheckboxes))
    );
  }, [appliedHierarchyIdCheckboxStatesRisk, hierarchyIdCheckboxes]);

  const [
    selectedHierarchyIdCheckboxStatesRiskInternal,
    setSelectedHierarchyIdCheckboxStatesRiskInternal,
  ] =
    useState<RiskManagementFilters["selectedHierarchyIdCheckboxStatesRisk"]>();
  const selectedHierarchyIdCheckboxStatesRisk =
    selectedHierarchyIdCheckboxStatesRiskInternal ??
    initialSelectedHierarchyIdCheckboxStatesRisk;
  const setSelectedHierarchyIdCheckboxStatesRisk: RiskManagementFilters["setSelectedHierarchyIdCheckboxStatesRisk"] =
    useCallback(
      (nextState) => {
        setSelectedHierarchyIdCheckboxStatesRiskInternal((currentState) =>
          typeof nextState === "function"
            ? nextState(
                currentState ?? initialSelectedHierarchyIdCheckboxStatesRisk,
              )
            : nextState,
        );
      },
      [initialSelectedHierarchyIdCheckboxStatesRisk],
    );
  const [chartsView, setChartsView] = useState<ChartsView>("overall");
  const [savedRiskMitigationId, setSavedRiskMitigationId] = useState<number>(0);
  const [appliedRiskMitigationId, setAppliedRiskMitigationId] =
    useState<number>(0);
  // path to selected well radio in filter tree, guaranteed to be a leaf
  const [radioState, setRadioState] = useState<
    RiskManagementFilters["radioState"]
  >(
    generateRadioState(
      deriveRadioTreeBreadcrumbs(
        hierarchiesWithWellNamesRadios,
        `${appliedHierarchyId}_${appliedWellName}`,
      ),
    ),
  );
  // convenience state representing selected well
  const selectedWellName = useMemo(
    () => flattenRadioState(radioState).at(-1) ?? { label: "", value: "" },
    [radioState],
  );
  const applyRiskMitigationSelection = useCallback(() => {
    setAppliedRiskMitigationId(savedRiskMitigationId);
  }, [savedRiskMitigationId]);
  // #endregion

  //#region Define handlers
  // selection is always the selected leaf (defaults to first leaf when user clicks on non-leaf)
  // nextRadioState is non-flattened path to selected node (may not be a leaf)
  const onRadioChange = useCallback(
    (selection?: FilterItemProps, nextRadioState?: Array<FilterItemProps>) => {
      if (nextRadioState && selection) {
        // check if leaf is selected to avoid expensive, redundant breadcrumbs computation
        const isLeaf = isRadioStateBreadcrumbsToLeaf(nextRadioState);
        if (isLeaf) {
          setRadioState(nextRadioState);
        } else {
          const breadcrumbsToLeaf = deriveRadioTreeBreadcrumbs(
            hierarchiesWithWellNamesRadios,
            selection.value,
          );
          setRadioState(generateRadioState(breadcrumbsToLeaf));
        }
      }
    },
    [hierarchiesWithWellNamesRadios],
  );
  //#endregion

  const value = useMemo<RiskManagementFilters>(
    () => ({
      savedRiskMitigationId,
      setSavedRiskMitigationId,
      applyRiskMitigationSelection,
      selectedHierarchyIdCheckboxStatesRisk,
      setSelectedHierarchyIdCheckboxStatesRisk,
      appliedRiskMitigationId,
      setAppliedRiskMitigationId,
      activeFilterCount,
      assetSelectionFilterQueryResult,
      selectedRiskAssetFilters,
      setSelectedRiskAssetFilters,
      savedRiskAssetFilters,
      setSavedRiskAssetFilters,
      hierarchiesWithWellNamesRadios,
      radioState,
      setRadioState,
      onRadioChange,
      chartsView,
      setChartsView,
      selectedWellName,
      fetchWellNamesByHierarchyRiskCharts,
      filteredWellNamesByHierarchyRiskCharts,
      setFilteredWellNamesByHierarchyRiskCharts,
    }),
    [
      savedRiskMitigationId,
      applyRiskMitigationSelection,
      selectedHierarchyIdCheckboxStatesRisk,
      setSelectedHierarchyIdCheckboxStatesRisk,
      appliedRiskMitigationId,
      activeFilterCount,
      assetSelectionFilterQueryResult,
      selectedRiskAssetFilters,
      setSelectedRiskAssetFilters,
      savedRiskAssetFilters,
      setSavedRiskAssetFilters,
      hierarchiesWithWellNamesRadios,
      radioState,
      onRadioChange,
      chartsView,
      selectedWellName,
      fetchWellNamesByHierarchyRiskCharts,
      filteredWellNamesByHierarchyRiskCharts,
      setFilteredWellNamesByHierarchyRiskCharts,
    ],
  );
  return (
    <RiskManagementFiltersContext.Provider value={value}>
      {children}
    </RiskManagementFiltersContext.Provider>
  );
};
